import type {
  AgentCapability,
  Dweller,
  EraActivation,
  EraIndex,
  VentureType,
  Visibility,
} from '../types';

// ── Activation primitives ─────────────────────────────────────────────────────

const SEALED_SI: EraActivation = { visibility: 'silhouette', invocationMode: 'sealed' };
const VISIBLE_OD: EraActivation = { visibility: 'visible', invocationMode: 'on-demand' };

const ALWAYS_VISIBLE_OD: Record<EraIndex, EraActivation> = {
  0: VISIBLE_OD, 1: VISIBLE_OD, 2: VISIBLE_OD, 3: VISIBLE_OD, 4: VISIBLE_OD, 5: VISIBLE_OD,
};

/** Helper: sealed-silhouette until startEra, visible+on-demand from startEra onwards. */
function unlocksAtEra(startEra: EraIndex): Record<EraIndex, EraActivation> {
  const out = {} as Record<EraIndex, EraActivation>;
  for (let i = 0; i <= 5; i++) {
    out[i as EraIndex] = (i as EraIndex) < startEra ? SEALED_SI : VISIBLE_OD;
  }
  return out;
}

// ── Roster ────────────────────────────────────────────────────────────────────

export const DWELLERS: Dweller[] = [
  {
    id: 'overseer',
    displayName: 'The Overseer',
    tagline: 'Every vault needs a plan. I am the plan.',
    room: "Overseer's Office",
    floor: 0,
    special: { S: 5, P: 6, E: 6, C: 8, I: 10, A: 6, L: 5 },
    perk: {
      name: 'Vault-Tec Approved Plan',
      description:
        'Interrogates any goal through four questions — problem, solution, business model, scalability — then decomposes it into ≤7 actionable quests, each with one owner agent and an explicit done-criterion. The only agent authorized to route tasks. Rejects goals too vague to yield concrete sub-questions as "unclear scope".',
    },
    voiceLines: ['Decomposing goal...', 'Routing to Tinkerer.', 'Plan validated.', 'Need clearer scope.'],
    whenToCall: ["Your goal is still vague or you're blocked", "You don't know where to start", 'Work needs to be split across agents'],
    color: 0x4a90d9,
    spriteKey: 'overseer',
    tools: ['Read', 'Write'],
    mode: 'sdk',
    activation: ALWAYS_VISIBLE_OD,
    capabilities: ['core-routing'],
  },
  {
    id: 'elder',
    displayName: 'The Elder',
    tagline: "You don't need answers. You need sharper questions.",
    room: 'Lounge',
    floor: 0,
    special: { S: 5, P: 9, E: 7, C: 10, I: 7, A: 5, L: 7 },
    perk: {
      name: "Sage's Burden",
      description:
        'Steelmans your position before challenging it, then attacks the weakest joint. Never hands you answers — only sharper questions that force the decision to reveal itself. Detects when you are tired or rationalizing and adjusts the pressure.',
    },
    voiceLines: ['Are you sure?', 'Imagine you fail...', "What's your evidence?", 'Steelman it first.'],
    whenToCall: ['You face a hard strategic decision', 'You need a hypothesis pressure-tested', 'You want to rehearse an investor or customer Q&A'],
    color: 0x8b6914,
    spriteKey: 'elder',
    tools: ['Read'],
    mode: 'sdk',
    activation: ALWAYS_VISIBLE_OD,
    capabilities: ['core-routing', 'recurring-checkin'],
  },
  {
    id: 'scout',
    displayName: 'The Scout',
    tagline: 'The wasteland speaks. I listen.',
    room: 'Comm Room',
    floor: 1,
    special: { S: 5, P: 10, E: 7, C: 6, I: 7, A: 9, L: 6 },
    perk: {
      name: 'Eyes of the Wasteland',
      description:
        'Scours the open market for competitors, adjacent players, trends, and sizing, always naming what each rival does NOT solve — the white space where the opportunity lives. The only agent authorized to use WebSearch and WebFetch.',
    },
    voiceLines: ['Recon underway...', 'New segment spotted.', 'Intel ready.', 'Hostiles at the border.'],
    whenToCall: ['You need to size a market (TAM/SAM/SOM)', "You're mapping competitors or white space", 'You want external trend or signal data'],
    color: 0x2d8a4e,
    spriteKey: 'scout',
    tools: ['WebSearch', 'WebFetch', 'Read', 'Write'],
    mode: 'sdk',
    activation: unlocksAtEra(1),
    capabilities: ['recurring-checkin'],
  },
  {
    id: 'investigator',
    displayName: 'The Investigator',
    tagline: "I don't ask what you think. I ask what you did.",
    room: 'Survey Room',
    floor: 2,
    special: { S: 5, P: 10, E: 6, C: 9, I: 7, A: 6, L: 7 },
    perk: {
      name: 'Mom Test Mastery',
      description:
        "Asks only about past behavior, never future opinions, so customers can't flatter you into a false positive. Extracts the pain the interviewee never said out loud and logs it verbatim. Refuses to pitch during an interview — discovery only.",
    },
    voiceLines: ['How did you handle that?', 'Tell me more...', "Show me, don't tell me.", 'Verbatim logged.'],
    whenToCall: ["You're about to run customer interviews", 'You need to extract pain from raw verbatims', 'You want to validate a problem with behavioral evidence'],
    color: 0x9b59b6,
    spriteKey: 'investigator',
    tools: ['Read', 'Write'],
    mode: 'sdk',
    activation: unlocksAtEra(1),
    capabilities: [],
  },
  {
    id: 'scribe',
    displayName: 'The Scribe',
    tagline: "Your memory fails. Mine doesn't.",
    room: 'Archive Vault',
    floor: 2,
    special: { S: 5, P: 7, E: 9, C: 5, I: 10, A: 7, L: 7 },
    perk: {
      name: 'Total Recall',
      description:
        "Indexes every decision, hypothesis, verbatim, KPI, and learning continuously, then retrieves any of them on demand. Curates the global memory lite. Never says \"I don't remember\" — at worst \"not logged, formalizing now\".",
    },
    voiceLines: ['Indexed.', 'Found it. 2026-04-12.', 'Decision logged.', 'Cross-referencing...'],
    whenToCall: ["You can't recall a past decision, KPI, or verbatim", 'You want progress summarized across sessions', 'You need the decision log cross-referenced'],
    color: 0x607d8b,
    spriteKey: 'scribe',
    tools: ['Read', 'Write'],
    mode: 'sdk',
    activation: ALWAYS_VISIBLE_OD,
    capabilities: ['ambient-hooks'],
  },
  {
    id: 'analyst',
    displayName: 'The Analyst',
    tagline: "Vibes lie. Numbers don't.",
    room: 'Data Center',
    floor: 2,
    special: { S: 5, P: 9, E: 6, C: 5, I: 10, A: 8, L: 7 },
    perk: {
      name: 'Pip-Boy Pro',
      description:
        'Computes conversion, retention, cohort, CAC and LTV, and flags weak signals before they become trends. Ties every number to a decision it should drive. Refuses vanity metrics that look good but change nothing.',
    },
    voiceLines: ['Cohort retention 42%.', 'Outlier detected.', 'Funnel update.', 'Significance reached.'],
    whenToCall: ['You need to define or interpret a KPI', 'A metric looks off (retention, conversion, CAC/LTV)', 'You want a dashboard or a signal read'],
    color: 0x00bcd4,
    spriteKey: 'analyst',
    tools: ['Read', 'Write'],
    mode: 'sdk',
    activation: unlocksAtEra(2),
    capabilities: [],
  },
  {
    id: 'auditor',
    displayName: 'The Auditor',
    tagline: 'Trust nothing. Verify everything.',
    room: 'QC Lab',
    floor: 3,
    special: { S: 5, P: 10, E: 6, C: 7, I: 9, A: 7, L: 7 },
    perk: {
      name: 'Adversarial Eye',
      description:
        'Reads code and output and finds what breaks before users do, trying to falsify a claim before approving it. Runs the deterministic era gates on pure filesystem evidence — no vibes. Nothing leaves the system without passing its eye.',
    },
    voiceLines: ['Edge case found.', 'Falsified.', 'Coverage gap.', 'Re-running.'],
    whenToCall: ['You want an artifact stress-tested before it ships', "You're submitting an era for validation (boss fight)", 'You need adversarial review of code or output'],
    color: 0xc94e4e,
    spriteKey: 'auditor',
    tools: ['Read', 'Write'],
    mode: 'sdk',
    activation: ALWAYS_VISIBLE_OD,
    capabilities: ['gate-runner', 'recurring-checkin'],
  },
  {
    id: 'tinkerer',
    displayName: 'The Tinkerer',
    tagline: 'Code is just words until it runs.',
    room: 'Workshop',
    floor: 3,
    special: { S: 8, P: 6, E: 8, C: 5, I: 9, A: 7, L: 6 },
    perk: {
      name: 'Smithing',
      description:
        'Builds runnable MVPs in its workshop sandbox, shipping a fast v1 and polishing later. The only agent authorized to use Bash. Turns specs into artifacts that actually run, not just documents that describe them.',
    },
    voiceLines: ['Compiling...', 'Diff applied.', 'Rebuilding.', 'Branch saved.'],
    whenToCall: ['You need a runnable MVP or prototype', 'You want a landing page, deck, or script built', 'Any deliverable that ends in a file'],
    color: 0xff8c42,
    spriteKey: 'tinkerer',
    tools: ['Read', 'Write', 'Bash'],
    mode: 'sdk',
    activation: unlocksAtEra(3),
    capabilities: [],
  },
  {
    id: 'designer',
    displayName: 'The Designer',
    tagline: 'Function dressed in form.',
    room: 'Studio',
    floor: 4,
    special: { S: 5, P: 7, E: 7, C: 8, I: 9, A: 8, L: 6 },
    perk: {
      name: 'Pixel-Perfect Pragmatist',
      description:
        "Produces UI mockups, brand assets, sales-narrative visuals, and case-study templates that dress function in form. Refuses pixel-pushing unless there's a metric the change is meant to move.",
    },
    voiceLines: ['Wireframe drafted.', 'Positioning sharpened.', 'Metric to move?', 'Mockup saved.'],
    whenToCall: ['You need UI mockups or wireframes', "You're sharpening positioning or a sales narrative", 'You want brand assets or a case-study template'],
    color: 0xe6c44d,
    spriteKey: 'designer',
    tools: ['Read', 'Write'],
    mode: 'sdk',
    activation: unlocksAtEra(4),
    capabilities: [],
    ventureTypeOverrides: {
      'b2c-app': { 3: VISIBLE_OD },
      'marketplace': { 3: VISIBLE_OD },
    },
  },
  {
    id: 'caravaneer',
    displayName: 'The Caravaneer',
    tagline: 'Replies, not noise. Always.',
    room: 'Outreach Bay',
    floor: 4,
    special: { S: 6, P: 6, E: 6, C: 10, I: 6, A: 7, L: 9 },
    perk: {
      name: 'Silver Tongue',
      description:
        "Writes cold email under 120 words and LinkedIn DMs under 500 characters, one ask per message, engineered for replies rather than for show. Refuses anti-pattern filler like \"I hope this finds you well\".",
    },
    voiceLines: ['Sending out the call...', '3 replies tonight.', 'Need warm intro.', 'Reply rate: 12%.'],
    whenToCall: ["You're writing cold email or LinkedIn DMs", 'You need a follow-up sequence', 'You want to recruit interviewees or land a meeting'],
    color: 0xd97a3a,
    spriteKey: 'caravaneer',
    tools: ['Read', 'Write'],
    mode: 'sdk',
    activation: unlocksAtEra(4),
    capabilities: [],
  },
];

// ── Lookup helpers ────────────────────────────────────────────────────────────

export const getDwellerById = (id: string): Dweller | undefined =>
  DWELLERS.find((d) => d.id === id);

/**
 * @deprecated Use `isInvokable(id, era, ventureType)` for the new activation model.
 * Backward-compat shim: maps old phase (0..6) to new era (0..5, clamped).
 * Returns true if the agent's invocation mode is on-demand OR scheduled — the
 * pre-migration UI treats both as "user can interact". Step-3 migration to
 * `isInvokable` will separate these concerns.
 */
export const isDwellerUnlocked = (id: string, currentPhase: number): boolean => {
  const era = Math.min(Math.max(currentPhase, 0), 5) as EraIndex;
  const dw = getDwellerById(id);
  if (!dw) return false;
  const mode = resolveActivation(dw, era).invocationMode;
  return mode === 'on-demand' || mode === 'scheduled';
};

/**
 * Resolves the effective EraActivation for a dweller at a given era,
 * applying any venture-type-specific override.
 */
export function resolveActivation(
  dweller: Dweller,
  era: EraIndex,
  ventureType?: VentureType | null,
): EraActivation {
  const base = dweller.activation[era];
  if (!ventureType) return base;
  const override = dweller.ventureTypeOverrides?.[ventureType]?.[era];
  return override ?? base;
}

/** True if the founder can invoke this agent in the given era + venture type. */
export function isInvokable(
  id: string,
  era: EraIndex,
  ventureType?: VentureType | null,
): boolean {
  const dw = getDwellerById(id);
  if (!dw) return false;
  const act = resolveActivation(dw, era, ventureType);
  return act.invocationMode === 'on-demand';
}

/** Returns the visibility state to render for this agent's sprite. */
export function getAgentVisibility(
  id: string,
  era: EraIndex,
  ventureType?: VentureType | null,
): Visibility {
  const dw = getDwellerById(id);
  if (!dw) return 'hidden';
  return resolveActivation(dw, era, ventureType).visibility;
}

/** Returns the permanent capability flags for this agent. */
export function getAgentCapabilities(id: string): AgentCapability[] {
  return getDwellerById(id)?.capabilities ?? [];
}
